import {
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

type RetryNavigation = () => void;

type UseUnsavedChangesGuardParams = {
  enabled: boolean;
  shouldBlock?: () => boolean;
  onBlockedNavigation: (retryNavigation: RetryNavigation) => void;
};

type BlockableNavigator = {
  block?: (blocker: (transition: { retry: RetryNavigation }) => void) => () => void;
};

const useUnsavedChangesGuard = ({
  enabled,
  shouldBlock,
  onBlockedNavigation,
}: UseUnsavedChangesGuardParams) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const enabledRef = useRef(enabled);
  const shouldBlockRef = useRef(shouldBlock);
  const blockedNavigationRef = useRef(onBlockedNavigation);

  useEffect(() => {
    enabledRef.current = enabled;
    shouldBlockRef.current = shouldBlock;
  }, [enabled, shouldBlock]);

  useEffect(() => {
    blockedNavigationRef.current = onBlockedNavigation;
  }, [onBlockedNavigation]);

  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [enabled]);

  useEffect(() => {
    const blockableNavigator = navigator as BlockableNavigator;
    if (typeof blockableNavigator.block !== "function") return;

    const unblock = blockableNavigator.block((transition) => {
      const canBlock = shouldBlockRef.current
        ? shouldBlockRef.current()
        : enabledRef.current;

      if (!canBlock) {
        transition.retry();
        return;
      }

      blockedNavigationRef.current(() => {
        unblock();
        transition.retry();
      });
    });

    return unblock;
  }, [navigator]);

  return useCallback(
    (navigation: RetryNavigation) => {
      const canBlock = shouldBlockRef.current
        ? shouldBlockRef.current()
        : enabledRef.current;

      if (canBlock) {
        onBlockedNavigation(navigation);
      } else {
        navigation();
      }
    },
    [onBlockedNavigation],
  );
};

export default useUnsavedChangesGuard;
